import { omit, pick } from 'ramda';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { BULK_CREATE_LOCATION_POSITION_IDS } from 'constants/rateSettings';
import moment from 'moment-timezone';
import { isNaN } from 'lodash';
import { PARENT_SETTINGS_FIELD } from './shared/position-rate-settings-form/constants';

const LOCATION_LEVEL_RATES = [
  'location_rate_setting',
  'agency_location_rate_setting',
  'location_position_rate_setting',
  'agency_location_position_rate_setting',
];

const w2HashKeysToDuplicate = [
  'charge_max_weekly_regular_hours',
  'charge_seventh_day_rule_enabled',
  'payout_max_weekly_regular_hours',
  'payout_seventh_day_rule_enabled',
];

const w2SubHashKeysToDuplicate = [
  'charge_double_overtime_multiplier',
  'charge_max_daily_overtime_hours',
  'charge_max_daily_regular_hours',
  'charge_overtime_multiplier',
  'payout_double_overtime_multiplier',
  'payout_max_daily_overtime_hours',
  'payout_max_daily_regular_hours',
  'payout_overtime_multiplier',
];

const getEmptyOrNilKeys = (obj: Record<string, any>): string[] => {
  return Object.keys(obj).filter((key) => {
    if (typeof obj[key] === 'string') {
      return isEmpty(obj[key]);
    }
    return isNil(obj[key]);
  });
};

const sanitizeRateTypeRoot = (
  parent: Record<string, any>,
  key: string,
  markup: number,
  isLocationLevelRate: boolean,
) => {
  const subSetting = parent[key];
  if (isNil(subSetting)) {
    return;
  }

  const subKeysToOmit = getEmptyOrNilKeys(subSetting);
  subKeysToOmit.push('__typename');

  const isUsingBlendedRates = subSetting['use_blended_rate'];
  if (subSetting.blended_rates && !isNil(markup)) {
    subSetting['blended_rates']['blended_rate_markup'] = markup;
  }

  if (isLocationLevelRate) {
    if (isUsingBlendedRates === true) {
      subKeysToOmit.push('non_blended_rates');
    } else if (isUsingBlendedRates === false) {
      subKeysToOmit.push('blended_rates');
    } else {
      subKeysToOmit.push('blended_rates', 'non_blended_rates', 'use_blended_rate');
    }
  }

  parent[key] = omit(subKeysToOmit, subSetting);
  if (isEmpty(parent[key])) {
    parent = omit([key], parent);
  }
};

const sanitizeRateTypeSubSection = (parent: Record<string, any>, key: string, markup: number) => {
  ['blended_rates', 'non_blended_rates'].forEach((rateTypeSettingKey) => {
    const rateTypeSetting = parent[key][rateTypeSettingKey];
    if (isNil(rateTypeSetting)) {
      return;
    }

    const rateTypeKeysToOmit = getEmptyOrNilKeys(rateTypeSetting);
    rateTypeKeysToOmit.push('__typename');

    if (isNil(markup)) {
      rateTypeKeysToOmit.push('blended_rate_markup');
    }

    parent[key][rateTypeSettingKey] = omit(rateTypeKeysToOmit, rateTypeSetting);

    if (isEmpty(parent[key][rateTypeSettingKey])) {
      parent[key] = omit([rateTypeSettingKey], parent[key]);
    }
  });

  if (isEmpty(parent[key])) {
    parent = omit([key], parent);
  }
};

export const getTransform = (rateLevels: string[], settingsName: string) => (data: any) => {
  const values = {
    ...data,
    settings: {
      ...data[settingsName],
    },
  };
  const settings = values.settings;

  if (settingsName === 'market_location_type_position_rate_setting') {
    values.active = values.market_location_type_position_rate_setting_active;
  }
  if (settingsName === 'state_location_type_position_rate_setting') {
    values.active = values.state_location_type_position_rate_setting_active;
  }

  const isLocationLevelRate = LOCATION_LEVEL_RATES.includes(settingsName);

  const keysToOmit = getEmptyOrNilKeys(settings);

  keysToOmit.push(
    'pro_rate_cents',
    'use_blended_rate',
    'blended_rate_markup',
    'blended_rate_cents',
    '__typename',
    PARENT_SETTINGS_FIELD,
  );
  values.settings = omit(keysToOmit, settings);

  ['ten99', 'w2'].forEach((subSettingKey) => {
    const subSetting = values.settings[subSettingKey];
    if (isNil(subSetting)) {
      return;
    }

    const rawTen99Markup = subSetting?.['blended_rates']?.['blended_rate_markup'];
    const formattedTen99Markup =
      rawTen99Markup === null || rawTen99Markup === undefined ? null : Number(rawTen99Markup);
    const ten99markup = isNaN(formattedTen99Markup) ? null : formattedTen99Markup;

    if (subSettingKey === 'ten99' && subSetting.margin_percent_1099) {
      values.settings.ten99.margin_percent_1099 = Number(subSetting.margin_percent_1099);
    }

    const subKeysToOmit = getEmptyOrNilKeys(subSetting);
    subKeysToOmit.push(
      'total_margin_percent_w2',
      'pro_rate_cents',
      'blended_rate_markup',
      'blended_rate_cents',
      'lockedRateType',
      '__typename',
    );
    values.settings[subSettingKey] = omit(subKeysToOmit, subSetting);

    if (subSettingKey === 'w2') {
      // Duplicate weekly overtime settings at the root level of Local to Travel
      if (!values.settings.w2.travel) {
        values.settings.w2.travel = {};
      }
      w2HashKeysToDuplicate.forEach((key) => {
        if (!isNil(values.settings.w2.local[key])) {
          values.settings.w2.travel[key] = values.settings.w2.local[key];
        } else {
          values.settings.w2.travel = omit([key], values.settings.w2.travel);
        }
      });
      // Duplicate sub-hash daily overtime settings of Local to Travel
      ['non_blended_rates', 'blended_rates'].forEach((rateType) => {
        if (!values.settings.w2.travel[rateType]) {
          values.settings.w2.travel[rateType] = {};
        }
        w2SubHashKeysToDuplicate.forEach((subHashKey) => {
          if (!isNil(values.settings.w2.local[rateType]?.[subHashKey])) {
            values.settings.w2.travel[rateType][subHashKey] =
              values.settings.w2.local[rateType][subHashKey];
          } else {
            values.settings.w2.travel[rateType] = omit(
              [subHashKey],
              values.settings.w2.travel[rateType],
            );
          }
        });
      });

      ['local', 'travel'].forEach((w2Key) => {
        const w2Subsetting = values.settings[subSettingKey][w2Key];

        const rawW2Markup = w2Subsetting?.['blended_rates']?.['blended_rate_markup'];
        const formattedW2Markup =
          rawW2Markup === null || rawW2Markup === undefined ? null : Number(rawW2Markup);
        const w2Markup = isNaN(formattedW2Markup) ? null : formattedW2Markup;
        const rawW2TotalMargin = w2Subsetting?.['total_margin_percent_w2'];
        const formattedW2TotalMargin =
          rawW2TotalMargin === null || rawW2TotalMargin === undefined
            ? null
            : Number(rawW2TotalMargin);
        const w2TotalMargin = isNaN(formattedW2TotalMargin) ? null : formattedW2TotalMargin;

        const subKeysToOmit = getEmptyOrNilKeys(subSetting);
        subKeysToOmit.push(
          'total_margin_percent_w2',
          'pro_rate_cents',
          'blended_rate_markup',
          'blended_rate_cents',
          'lockedRateType',
          '__typename',
        );

        values.settings[subSettingKey][w2Key] = omit(subKeysToOmit, w2Subsetting);

        sanitizeRateTypeRoot(values.settings.w2, w2Key, w2Markup, isLocationLevelRate);
        sanitizeRateTypeSubSection(values.settings.w2, w2Key, w2Markup);

        const marginField =
          w2Key === 'local' ? 'base_margin_percent_w2' : 'margin_percent_w2_travel';

        if (w2Subsetting[marginField]) {
          values.settings.w2[w2Key][marginField] = Number(w2Subsetting[marginField]);
        }

        if (w2Key === 'local' && !isNil(w2TotalMargin)) {
          values.settings.w2['local']['total_margin_percent_w2'] = w2TotalMargin;
        }

        if (!isNil(w2Markup) && !isLocationLevelRate) {
          values.settings.w2[w2Key]['blended_rates']['blended_rate_markup'] = w2Markup;
        }

        if (isEmpty(values.settings.w2[w2Key])) {
          values.settings.w2 = omit([w2Key], values.settings.w2);
        }
      });
    } else {
      sanitizeRateTypeRoot(values.settings, subSettingKey, ten99markup, isLocationLevelRate);
      sanitizeRateTypeSubSection(values.settings, subSettingKey, ten99markup);
    }

    if (isEmpty(values.settings[subSettingKey])) {
      values.settings = omit([subSettingKey], values.settings);
    }
  });

  return pick(
    [...rateLevels, 'position_id', 'specialty_id', 'active', 'settings', 'last_rate_review'],
    values,
  );
};

export const transformBulkLocationPosition = (data) => ({
  create_multiple: true,
  id: data.location_rate_settings?.id,
  location_position_rate_settings: BULK_CREATE_LOCATION_POSITION_IDS.flatMap((position_id) =>
    data[position_id].enabled
      ? {
          location_id: data.id,
          position_id,
          expires_at: moment
            .tz(data[position_id].expires_at, data.timezone_lookup)
            .endOf('day')
            .format(),
          settings:
            data?.primary_billing_group?.plan === 'enterprise_w2'
              ? {
                  w2: {
                    local: {
                      charge_rate_cents: data[position_id].charge_rate_cents,
                    },
                  },
                }
              : {
                  ten99: {
                    charge_rate_cents: data[position_id].charge_rate_cents,
                  },
                },
        }
      : [],
  ),
});
